/**
 * Cookie Consent Hook
 * Main state management for the cookie consent feature
 */

import { useState, useCallback, useEffect } from 'react';
import type { CookiePreferences, CookieConsentState, CookieConsentActions } from '../types';
import { CONSENT_MANAGED_BY_GTM, DEFAULT_PREFERENCES } from '../constants';
import { getStoredConsent, saveConsent, hasConsent as checkHasConsent } from '../utils/cookieStorage';

declare global {
  interface Window {
    /**
     * Re-opens the production CMP popup. Defined by the GTM-loaded
     * `cookie_set_prod_v20.js` (legacy footer used `onclick="window.showCookieConsent();"`).
     */
    showCookieConsent?: () => void;
  }
}

export interface UseCookieConsentReturn extends CookieConsentState, CookieConsentActions {}

/**
 * Hook for managing cookie consent state and actions
 */
export function useCookieConsent(): UseCookieConsentReturn {
  const [preferences, setPreferences] = useState<CookiePreferences>(() => ({
    ...DEFAULT_PREFERENCES,
  }));
  const [hasConsented, setHasConsented] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  // Load stored consent on mount
  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setPreferences(stored.preferences);
      setHasConsented(true);
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const allEnabled: CookiePreferences = {
      necessary: true,
      performance: true,
      partners: true,
      advertising: true,
    };
    setPreferences(allEnabled);
    saveConsent(allEnabled);
    setHasConsented(true);
    setShowBanner(false);
    setShowDialog(false);
  }, []);

  // Accept only selected cookies
  const acceptSelected = useCallback(() => {
    saveConsent(preferences);
    setHasConsented(true);
    setShowBanner(false);
    setShowDialog(false);
  }, [preferences]);

  // Update a single preference
  const updatePreference = useCallback(
    (key: keyof Omit<CookiePreferences, 'necessary'>, value: boolean) => {
      setPreferences((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  // Open settings dialog
  const openDialog = useCallback(() => {
    // When the production CMP (loaded via GTM) owns consent, re-open ITS popup
    // through the global it exposes — matching the legacy footer's
    // `window.showCookieConsent()` — instead of our in-house dialog.
    if (CONSENT_MANAGED_BY_GTM) {
      if (typeof window !== 'undefined' && typeof window.showCookieConsent === 'function') {
        window.showCookieConsent();
      }
      return;
    }
    setShowDialog(true);
    setShowBanner(false);
  }, []);

  // Close settings dialog
  const closeDialog = useCallback(() => {
    setShowDialog(false);
    // Show banner again if user hasn't consented
    if (!checkHasConsent()) {
      setShowBanner(true);
    }
  }, []);

  // Check if a category is allowed
  const isAllowed = useCallback(
    (category: keyof CookiePreferences): boolean => {
      if (!hasConsented) {
        return category === 'necessary';
      }
      return preferences[category];
    },
    [hasConsented, preferences]
  );

  return {
    // State
    preferences,
    hasConsented,
    showBanner,
    showDialog,
    // Actions
    acceptAll,
    acceptSelected,
    updatePreference,
    openDialog,
    closeDialog,
    isAllowed,
  };
}
